import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  Card,
  Col,
  Empty,
  List,
  Row,
  Skeleton,
  Space,
  Statistic,
  Tag,
  Typography
} from "antd";
import { SyncOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import { formatDay, formatEuro } from "../workReports/format";
import { getOverview, syncNow } from "./api";
import {
  ACCEPTANCE_COLORS,
  CLAIM_COLORS,
  DEFECT_COLORS,
  GUARANTEE_COLORS,
  acceptanceStatusLabels,
  categoryLabels,
  claimStatusLabels,
  defectStatusLabels,
  guaranteeStatusLabels,
  guaranteeTypeLabels
} from "./labels";

/** Open protocols, defects, warranties ending and bonds due back — company-wide or per project. */
const Overview = ({ project, canUpdate, onOpenGuarantee }) => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = () =>
    getOverview(project)
      .then(res => setData(res.data))
      .catch(generalErrorAlert);

  useEffect(() => {
    load();
  }, [project]);

  const sync = async () => {
    setSyncing(true);
    try {
      await syncNow();
      Success(t("Deadlines updated"));
      load();
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setSyncing(false);
    }
  };

  if (!data) return <Skeleton active paragraph={{ rows: 8 }} />;
  const { kpis } = data;
  const accLabels = acceptanceStatusLabels(t);
  const defLabels = defectStatusLabels(t);
  const claimLabels = claimStatusLabels(t);
  const gLabels = guaranteeStatusLabels(t);
  const gTypes = guaranteeTypeLabels(t);
  const categories = categoryLabels(t);

  const tiles = [
    { key: "acc", title: t("Open acceptances"), value: kpis.openAcceptances },
    {
      key: "def",
      title: t("Open defects"),
      value: kpis.openDefects,
      extra: kpis.overdueDefects ? t("{{n}} overdue", { n: kpis.overdueDefects }) : ""
    },
    { key: "war", title: t("Warranties running"), value: kpis.activeWarranties },
    {
      key: "gua",
      title: t("Bonds given"),
      value: formatEuro(kpis.guaranteesOut, lng),
      extra: kpis.guaranteesDue ? t("{{n}} due back", { n: kpis.guaranteesDue }) : ""
    }
  ];

  return (
    <div className="wty-overview">
      <Row gutter={[12, 12]} className="mb-3">
        {tiles.map(tile => (
          <Col xs={12} lg={6} key={tile.key}>
            <Card size="small" className="wty-tile">
              <Statistic title={tile.title} value={tile.value} />
              {tile.extra && <Tag color="error">{tile.extra}</Tag>}
            </Card>
          </Col>
        ))}
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card
            size="small"
            title={t("Defects to remove")}
            extra={
              canUpdate && (
                <Button size="small" icon={<SyncOutlined />} loading={syncing} onClick={sync}>
                  {t("Update deadlines")}
                </Button>
              )
            }
          >
            {data.defects.length ? (
              <List
                size="small"
                dataSource={data.defects}
                renderItem={d => (
                  <List.Item>
                    <div className="wty-row">
                      <Link
                        to={
                          d.kind === "acceptance"
                            ? `/user/warranty/acceptances/${d.parent}`
                            : `/user/warranty/warranties/${d.parent}`
                        }
                      >
                        <Typography.Text strong>{d.description}</Typography.Text>
                      </Link>
                      <div className="wty-small">
                        {[d.label, d.project].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                    <Space size={4} wrap className="wty-row-tags">
                      {d.kind === "acceptance" ? (
                        <Tag color={DEFECT_COLORS[d.status]}>{defLabels[d.status]}</Tag>
                      ) : (
                        <Tag color={CLAIM_COLORS[d.status]}>{claimLabels[d.status]}</Tag>
                      )}
                      {d.deadline && (
                        <Tag color={d.overdue ? "error" : "default"}>{formatDay(d.deadline)}</Tag>
                      )}
                    </Space>
                  </List.Item>
                )}
              />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("No open defects")} />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card size="small" title={t("Warranties ending soon")} className="mb-3">
            {data.ending.length ? (
              <List
                size="small"
                dataSource={data.ending}
                renderItem={w => (
                  <List.Item>
                    <div className="wty-row">
                      <Link to={`/user/warranty/warranties/${w._id}`}>
                        <Typography.Text strong>{w.title}</Typography.Text>
                      </Link>
                      <div className="wty-small">
                        {w.periods.length
                          ? w.periods
                              .map(p => `${p.label || categories[p.category]}: ${formatDay(p.end)}`)
                              .join(" · ")
                          : formatDay(w.end)}
                      </div>
                      {w.nextInspection && (
                        <div className="wty-small">
                          {t("Suggested inspection from {{date}}", {
                            date: formatDay(w.nextInspection.date)
                          })}
                        </div>
                      )}
                    </div>
                    {w.openClaims > 0 && (
                      <Tag color="error">{t("{{n}} open claims", { n: w.openClaims })}</Tag>
                    )}
                  </List.Item>
                )}
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t("No warranty ends in the next six months")}
              />
            )}
          </Card>

          <Card size="small" title={t("Open acceptances")} className="mb-3">
            {data.openAcceptances.length ? (
              <List
                size="small"
                dataSource={data.openAcceptances}
                renderItem={a => (
                  <List.Item>
                    <div className="wty-row">
                      <Link to={`/user/warranty/acceptances/${a._id}`}>
                        <Typography.Text strong>{a.number}</Typography.Text>
                      </Link>{" "}
                      <span className="wty-small">
                        {[a.client, a.project].filter(Boolean).join(" · ")}
                      </span>
                      {a.deemedDate && (
                        <div className="wty-small">
                          {t("Deemed accepted after {{date}} unless refused", {
                            date: formatDay(a.deemedDate)
                          })}
                        </div>
                      )}
                    </div>
                    <Tag color={ACCEPTANCE_COLORS[a.status]}>{accLabels[a.status]}</Tag>
                  </List.Item>
                )}
              />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("No open acceptances")} />
            )}
          </Card>

          <Card size="small" title={t("Guarantee bonds")}>
            {data.guarantees.length ? (
              <List
                size="small"
                dataSource={data.guarantees}
                renderItem={g => (
                  <List.Item>
                    <div className="wty-row">
                      <Button
                        type="link"
                        className="wty-link"
                        onClick={() => onOpenGuarantee?.(g._id)}
                      >
                        {gTypes[g.type]} {g.bondNumber || g.number}
                      </Button>
                      <div className="wty-small">
                        {[g.issuer, formatEuro(g.amount, lng)].filter(Boolean).join(" · ")}
                        {g.returnDue
                          ? ` · ${t("due back {{date}}", { date: formatDay(g.returnDue) })}`
                          : ""}
                      </div>
                    </div>
                    <Space size={4} wrap className="wty-row-tags">
                      <Tag color={GUARANTEE_COLORS[g.status]}>{gLabels[g.status]}</Tag>
                      {g.returnOverdue && <Tag color="error">{t("Due back")}</Tag>}
                    </Space>
                  </List.Item>
                )}
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t("No open guarantee bonds")}
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Overview;
